import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { selectIncident } from 'slices/commonSelectors';
import { useUpdateMemberActivation } from './hooks/useUpdateMemberActivation';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { Spinner } from 'react-bootstrap';
import { RosterEntry } from '../IAP/hooks/incidentRoster/incidentRoster.types';

const ActivationConfirmation: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const { user_guid } = useSelector((state: any) => state.app.user);
  const incident = useSelector(selectIncident);

  const [showDeclineNote, setShowDeclineNote] = useState(false);
  const [declineNote, setDeclineNote] = useState('');

  const { data: rosterData, isLoading: isRosterLoading } =
    useGetRosterForIncident(incidentId || '');
  const updateMemberActivation = useUpdateMemberActivation();

  // Find current user's member data
  const currentUserMember = rosterData?.find(
    (member: any) => member.user_guid === user_guid
  ) as RosterEntry | undefined;

  useEffect(() => {
    if (!isRosterLoading && !currentUserMember) {
      toast.error('You are not a member of this incident');
      navigate('/');
    }
  }, [isRosterLoading, currentUserMember, navigate]);

  const handleActivationUpdate = (isActivated: boolean) => {
    if (!incidentId) return;

    updateMemberActivation.mutate(
      {
        incident_id: incidentId,
        member_user_guid: user_guid,
        is_activated: isActivated,
        decline_note: isActivated ? undefined : declineNote,
      },
      {
        onSuccess: () => {
          if (isActivated) {
            toast.success('Activation confirmed!');
            navigate(`/iap/${incidentId}/settings`);
          } else {
            toast.info('Activation declined');
            setShowDeclineNote(false);
            setDeclineNote('');
          }
        },
        onError: () => {
          toast.error('Failed to update activation status');
        },
      }
    );
  };

  if (isRosterLoading) {
    return (
      <div className="site-main">
        <div className="container-fluid">
          <div
            className="row justify-content-center align-items-center"
            style={{ minHeight: '80vh' }}
          >
            <div className="col-auto text-center">
              <Spinner animation="border" role="status" className="mb-2" />
              <div className="text-muted">Loading...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUserMember) {
    return null;
  }

  const {
    activatedTimestamp: activated_timestamp,
    declinedTimestamp: declined_timestamp,
    activationNote: activation_note,
  } = currentUserMember;
  const hasResponded = activated_timestamp || declined_timestamp;

  return (
    <div className="site-main">
      <div className="container-fluid">
        <div
          className="row justify-content-center align-items-center"
          style={{ minHeight: '80vh' }}
        >
          <div className="col-12 col-md-8 col-lg-6">
            <div className="bg-gray-800 rounded p-4 p-md-5">
              <h1 className="h3 mb-4 text-white">
                Incident Activation - {incident?.name || 'Loading...'}
              </h1>

              {hasResponded ? (
                <div>
                  {activated_timestamp && (
                    <div className="bg-success bg-opacity-10 border border-success rounded p-4 text-center mb-4">
                      <i className="fas fa-check-circle fa-2x mb-3 d-block text-success"></i>
                      <p className="mb-2 text-white">
                        You have already confirmed your activation for this
                        incident.
                      </p>
                      <small className="text-muted">
                        Confirmed at:{' '}
                        {new Date(activated_timestamp).toLocaleString()}
                      </small>
                    </div>
                  )}

                  {declined_timestamp && (
                    <div className="bg-danger bg-opacity-10 border border-danger rounded p-4 text-center mb-4">
                      <i className="fas fa-times-circle fa-2x mb-3 d-block text-danger"></i>
                      <p className="mb-2 text-white">
                        You have declined activation for this incident.
                      </p>
                      <small className="text-muted d-block">
                        Declined at:{' '}
                        {new Date(declined_timestamp).toLocaleString()}
                      </small>
                      {activation_note && (
                        <p className="fst-italic mt-3 mb-0 text-white">
                          Reason: {activation_note}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-4">
                    <p className="text-center text-muted mb-3">
                      Would you like to change your response?
                    </p>
                    {!showDeclineNote ? (
                      <div className="button-group justify-content-center">
                        {declined_timestamp && (
                          <StylishNewButton
                            className="button--primary"
                            onClick={() => handleActivationUpdate(true)}
                            disabled={updateMemberActivation.isPending}
                          >
                            Change to Activated
                          </StylishNewButton>
                        )}
                        {activated_timestamp && (
                          <StylishNewButton
                            className="button--secondary"
                            onClick={() => setShowDeclineNote(true)}
                            disabled={updateMemberActivation.isPending}
                          >
                            Change to Declined
                          </StylishNewButton>
                        )}
                        <StylishNewButton
                          className="button--tertiary"
                          onClick={() =>
                            navigate(`/iap/${incidentId}/settings`)
                          }
                        >
                          Go to Incident
                        </StylishNewButton>
                      </div>
                    ) : (
                      <div>
                        <StylishNewTextArea
                          className="mb-3"
                          placeholder="Enter decline reason (optional, max 160 characters)"
                          value={declineNote}
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>
                          ) => setDeclineNote(e.target.value.slice(0, 160))}
                          maxLength={160}
                          rows={3}
                          fullWidth
                        />
                        <div className="button-group justify-content-center">
                          <StylishNewButton
                            className="button--primary"
                            onClick={() => handleActivationUpdate(false)}
                            disabled={updateMemberActivation.isPending}
                          >
                            Submit Decline
                          </StylishNewButton>
                          <StylishNewButton
                            className="button--secondary"
                            onClick={() => {
                              setShowDeclineNote(false);
                              setDeclineNote('');
                            }}
                            disabled={updateMemberActivation.isPending}
                          >
                            Cancel
                          </StylishNewButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-center text-muted mb-3">
                    You have been requested to activate for this incident.
                  </p>
                  <p className="text-center text-muted mb-4">
                    Please confirm or decline your activation below:
                  </p>

                  {!showDeclineNote ? (
                    <div className="d-flex justify-content-center">
                      <StylishNewToggleButtonGroup
                        data={[
                          { label: 'Confirm Activation', value: 'activate' },
                          { label: 'Decline Activation', value: 'decline' },
                        ]}
                        selected={''}
                        onChange={(value) => {
                          if (value === 'activate') {
                            handleActivationUpdate(true);
                          } else {
                            setShowDeclineNote(true);
                          }
                        }}
                        disabled={updateMemberActivation.isPending}
                        className="justify-content-center"
                      />
                    </div>
                  ) : (
                    <div>
                      <StylishNewTextArea
                        className="mb-3"
                        placeholder="Enter decline reason (optional, max 160 characters)"
                        value={declineNote}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          setDeclineNote(e.target.value.slice(0, 160))
                        }
                        maxLength={160}
                        rows={3}
                        fullWidth
                      />
                      <div className="button-group justify-content-center">
                        <StylishNewButton
                          className="button--primary"
                          onClick={() => handleActivationUpdate(false)}
                          disabled={updateMemberActivation.isPending}
                        >
                          Submit Decline
                        </StylishNewButton>
                        <StylishNewButton
                          className="button--secondary"
                          onClick={() => {
                            setShowDeclineNote(false);
                            setDeclineNote('');
                          }}
                          disabled={updateMemberActivation.isPending}
                        >
                          Cancel
                        </StylishNewButton>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivationConfirmation;
